"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const progressContainerVariants = cva(
  "group relative flex w-full flex-col gap-2 transition-all duration-200",
  {
    variants: {
      variant: {
        card: [
          "rounded-lg border bg-card p-4 text-card-foreground shadow-sm",
          "hover:shadow-md",
        ],
        outline: ["rounded-lg border-2 p-4", "hover:border-primary/50"],
        ghost: ["rounded-lg p-4 hover:bg-muted/50"],
        minimal: "p-2",
      },
      size: {
        sm: "gap-1",
        md: "gap-2",
        lg: "gap-3",
      },
    },
    defaultVariants: {
      variant: "card",
      size: "md",
    },
  }
);

const progressTitleVariants = cva("font-semibold tracking-tight", {
  variants: {
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const progressDescriptionVariants = cva(
  "text-muted-foreground leading-relaxed",
  {
    variants: {
      size: {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const progressValueVariants = cva("font-mono font-semibold tabular-nums", {
  variants: {
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
    valueVariant: {
      default: "text-foreground",
      primary: "text-primary",
      muted: "text-muted-foreground",
      gradient:
        "bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent",
    },
  },
  defaultVariants: {
    size: "md",
    valueVariant: "default",
  },
});

const progressBarVariants = cva("w-full overflow-hidden rounded-full", {
  variants: {
    size: {
      sm: "h-1.5",
      md: "h-2",
      lg: "h-3",
    },
    barVariant: {
      default: "bg-primary/20",
      primary: "bg-primary/20",
      success: "bg-green-500/20",
      warning: "bg-yellow-500/20",
      danger: "bg-red-500/20",
      muted: "bg-muted",
    },
  },
  defaultVariants: {
    size: "md",
    barVariant: "default",
  },
});

const progressIndicatorVariants = cva("h-full transition-all", {
  variants: {
    barVariant: {
      default: "bg-primary",
      primary: "bg-primary",
      success: "bg-green-500",
      warning: "bg-yellow-500",
      danger: "bg-red-500",
      muted: "bg-muted-foreground",
    },
    animated: {
      true: "transition-all duration-500 ease-out",
      false: "",
    },
  },
  defaultVariants: {
    barVariant: "default",
    animated: true,
  },
});

export interface ProgressItemProps
  extends VariantProps<typeof progressContainerVariants> {
  animated?: boolean;
  ariaLabel?: string;
  barVariant?:
    | "default"
    | "primary"
    | "success"
    | "warning"
    | "danger"
    | "muted";
  className?: string;
  classNames?: {
    container?: string;
    header?: string;
    title?: string;
    value?: string;
    description?: string;
    progressBar?: string;
    progressIndicator?: string;
  };
  customValueFormatter?: (value: number, min: number, max: number) => string;
  description?: string;
  max?: number;
  min?: number;
  onClick?: () => void;
  showValue?: boolean;
  size?: "sm" | "md" | "lg";
  title?: string;
  value: number;
  valueFormat?: "percentage" | "fraction" | "value" | "custom";
  valueVariant?: "default" | "primary" | "muted" | "gradient";
  variant?: "card" | "outline" | "ghost" | "minimal";
}

export function ProgressItem({
  value,
  min = 0,
  max = 100,
  title,
  description,
  showValue = true,
  valueFormat = "percentage",
  customValueFormatter,
  variant = "card",
  size = "md",
  valueVariant = "default",
  barVariant = "default",
  animated = true,
  onClick,
  ariaLabel,
  className,
  classNames,
}: ProgressItemProps) {
  const percentage = Math.min(
    100,
    Math.max(0, ((value - min) / (max - min)) * 100)
  );

  const formatValue = (): string => {
    if (customValueFormatter) {
      return customValueFormatter(value, min, max);
    }

    switch (valueFormat) {
      case "percentage":
        return `${Math.round(percentage)}%`;
      case "fraction":
        return `${value} / ${max}`;
      case "value":
        return `${value}`;
      default:
        return `${Math.round(percentage)}%`;
    }
  };

  const isClickable = Boolean(onClick);

  const containerClassName = cn(
    progressContainerVariants({ variant, size }),
    isClickable && "cursor-pointer",
    classNames?.container,
    className
  );

  const progressAriaLabel =
    ariaLabel ??
    (title ? `${title}: ${formatValue()}` : `Progress: ${formatValue()}`);

  const content = (
    <>
      {(title ?? showValue) && (
        <div
          className={cn(
            "flex items-start justify-between gap-4",
            classNames?.header
          )}
        >
          {title && (
            <div
              className={cn(progressTitleVariants({ size }), classNames?.title)}
            >
              {title}
            </div>
          )}
          {showValue && (
            <div
              className={cn(
                progressValueVariants({ size, valueVariant }),
                classNames?.value
              )}
            >
              {formatValue()}
            </div>
          )}
        </div>
      )}

      {description && (
        <div
          className={cn(
            progressDescriptionVariants({ size }),
            classNames?.description
          )}
        >
          {description}
        </div>
      )}

      <div
        aria-label={progressAriaLabel}
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={value}
        className={cn(
          progressBarVariants({ size, barVariant }),
          classNames?.progressBar
        )}
        role="progressbar"
      >
        <div
          className={cn(
            progressIndicatorVariants({ barVariant, animated }),
            classNames?.progressIndicator
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </>
  );

  if (isClickable) {
    return (
      <button className={containerClassName} onClick={onClick} type="button">
        {content}
      </button>
    );
  }

  return <div className={containerClassName}>{content}</div>;
}

export interface ProgressCollectionProps
  extends VariantProps<typeof progressContainerVariants> {
  animated?: boolean;
  barVariant?:
    | "default"
    | "primary"
    | "success"
    | "warning"
    | "danger"
    | "muted";
  className?: string;
  columns?: 1 | 2 | 3;
  gap?: "sm" | "md" | "lg";
  items: ProgressItemProps[];
  layout?: "stack" | "grid";
  onItemClick?: (item: ProgressItemProps, index: number) => void;
  size?: "sm" | "md" | "lg";
  valueVariant?: "default" | "primary" | "muted" | "gradient";
  variant?: "card" | "outline" | "ghost" | "minimal";
}

const progressCollectionVariants = cva("w-full", {
  variants: {
    layout: {
      stack: "flex flex-col",
      grid: "grid",
    },
    columns: {
      1: "grid-cols-1",
      2: "grid-cols-1 md:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    },
    gap: {
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
    },
  },
  defaultVariants: {
    layout: "stack",
    gap: "md",
  },
});

export function ProgressCollection({
  items,
  layout = "stack",
  columns = 1,
  gap = "md",
  variant = "card",
  size = "md",
  valueVariant = "default",
  barVariant = "default",
  animated = true,
  onItemClick,
  className,
}: ProgressCollectionProps) {
  return (
    <div
      className={cn(
        progressCollectionVariants({
          layout,
          columns: layout === "grid" ? columns : undefined,
          gap,
        }),
        className
      )}
    >
      {items.map((item, index) => (
        <ProgressItem
          animated={item.animated ?? animated}
          barVariant={item.barVariant ?? barVariant}
          key={item.title ?? `progress-${index}`}
          onClick={
            onItemClick
              ? () => onItemClick(item as ProgressItemProps, index)
              : item.onClick
          }
          size={item.size ?? size}
          valueVariant={item.valueVariant ?? valueVariant}
          variant={item.variant ?? variant}
          {...item}
        />
      ))}
    </div>
  );
}
